<script lang="ts" setup>
defineProps({
  commentObj: { type: Object, default: () => ({
    rating: 0,
    likeCount: 0,
    watchCount: 0,
  }) },
});

const getRatingCount = (count) => {
  if (count === null || count === undefined || isNaN(count)) {
    return 0;
  }
  return count;
};

const getStarCount = (rating) => {
  if (!rating || isNaN(rating) || rating < 0) {
    return 0;
  }
  return Math.floor(Math.min(rating, 5));
};

const getEmptyStarCount = (rating) => {
  if (!rating || isNaN(rating) || rating < 0) {
    return 5;
  }
  const full = Math.floor(Math.min(rating, 5));
  const hasHalf = rating % 1 !== 0 && rating > 0 && rating <= 5;
  return 5 - full - (hasHalf ? 1 : 0);
};

const getProgressPercentage = (item, number) => {
  if (!number) {
    return 0;
  }
  const { rate1Count = 0, rate2Count = 0, rate3Count = 0, rate4Count = 0, rate5Count = 0 } = item || {};
  const total = rate1Count + rate2Count + rate3Count + rate4Count + rate5Count;
  if (!total) {
    return 0;
  }
  return (number / total) * 100;
};
</script>
<template>
  <div style="display:flex; justify-content:space-between; align-items:flex-end;">
    <div>
      <div class="main-stat" style="margin-bottom:0; color: var(--accent-orange);">{{ commentObj?.rating ? commentObj?.rating.toFixed(1) : "未評価" }}</div>
      <div class="star" style="font-size: 0.8rem; margin-bottom: 8px;">
        <i class="fas fa-star" v-for="i in getStarCount(commentObj?.rating)" :key="'full-' + i"></i>
        <i class="fas fa-star-half-alt" v-if="commentObj?.rating && commentObj?.rating % 1 !== 0 && commentObj?.rating > 0"></i>
        <i class="far fa-star empty-star" v-for="i in getEmptyStarCount(commentObj?.rating)" :key="'empty-' + i"></i>
      </div>
    </div>
    <div style="text-align: right;">
      <div style="font-size: 0.8rem; color: var(--text-secondary);">みたい登録数</div>
      <div style="font-size: 1.4rem; font-weight: 700; color: var(--accent-blue);">{{ getRatingCount(commentObj?.rateCount) }}</div>
      <div style="font-size: 0.8rem; color: var(--text-secondary);">みた登録数</div>
      <div style="font-size: 1.4rem; font-weight: 700; color: var(--accent-purple);">{{ getRatingCount(commentObj?.haveSeen) }}</div>
    </div>
  </div>

  <div style="margin-top: 15px; padding-top: 12px;">
    <div class="detail-row">
      <span class="detail-label" style="display: flex; align-items: center; gap: 4px;">
        <i class="fas fa-star" style="color: var(--brand-color); font-size: 0.7rem;"></i>5
      </span>
      <div class="detail-bar-container">
        <div class="detail-bar" :style="{ width: getProgressPercentage(commentObj, commentObj?.rate5Count) + '%', background: 'var(--brand-color)' }"></div>
      </div>
      <span class="detail-value">{{ getProgressPercentage(commentObj, commentObj?.rate5Count).toFixed(1) }}%</span>
    </div>
    <div class="detail-row">
      <span class="detail-label" style="display: flex; align-items: center; gap: 4px;">
          <i class="fas fa-star" style="color: var(--brand-color); font-size: 0.7rem;"></i>4
      </span>
      <div class="detail-bar-container">
          <div class="detail-bar" :style="{ width: getProgressPercentage(commentObj, commentObj?.rate4Count) + '%', background: 'var(--accent-orange)' }"></div>
      </div>
      <span class="detail-value">{{ getProgressPercentage(commentObj, commentObj?.rate4Count).toFixed(1) }}%</span>
    </div>
    <div class="detail-row">
      <span class="detail-label" style="display: flex; align-items: center; gap: 4px;">
          <i class="fas fa-star" style="color: var(--brand-color); font-size: 0.7rem;"></i>3
      </span>
      <div class="detail-bar-container">
          <div class="detail-bar" :style="{ width: getProgressPercentage(commentObj, commentObj?.rate3Count) + '%', background: 'var(--accent-blue)' }"></div>
      </div>
      <span class="detail-value">{{ getProgressPercentage(commentObj, commentObj?.rate3Count).toFixed(1) }}%</span>
    </div>
    <div class="detail-row">
      <span class="detail-label" style="display: flex; align-items: center; gap: 4px;">
          <i class="fas fa-star" style="color: var(--brand-color); font-size: 0.7rem;"></i>2
      </span>
      <div class="detail-bar-container">
          <div class="detail-bar" :style="{ width: getProgressPercentage(commentObj, commentObj?.rate2Count) + '%', background: 'var(--accent-purple)' }"></div>
      </div>
      <span class="detail-value">{{ getProgressPercentage(commentObj, commentObj?.rate2Count).toFixed(1) }}%</span>
    </div>
    <div class="detail-row">
      <span class="detail-label" style="display: flex; align-items: center; gap: 4px;">
          <i class="fas fa-star" style="color: var(--brand-color); font-size: 0.7rem;"></i>1
      </span>
      <div class="detail-bar-container">
          <div class="detail-bar" :style="{ width: getProgressPercentage(commentObj, commentObj?.rate1Count) + '%', background: 'var(--accent-red)' }"></div>
      </div>
      <span class="detail-value">{{ getProgressPercentage(commentObj, commentObj?.rate1Count).toFixed(1) }}%</span>
    </div>
  </div>
</template>
<style lang="less" scoped>
  .main-stat {
    font-size: 2rem !important;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
    color: var(--text-primary);
  }
  .star {
    color: var(--brand-color);
    font-size: 1.2rem;

    .empty-star {
      color: #d9d9d9;
    }
  }
  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--border-color);
    font-size: 0.85rem;

    &:last-child {
      border-bottom: none;
    }

    .detail-label {
      color: var(--text-secondary);
      min-width: 40px;
      font-weight: 500;
      flex-shrink: 0;
    }

    .detail-bar-container {
      flex: 1;
      margin: 0 10px;
      height: 6px;
      background: #f0f0f0;
      border-radius: 3px;
      overflow: hidden;

      .detail-bar {
        height: 100%;
        border-radius: 2px;
      }
    }

    .detail-value {
      font-weight: 700;
      text-align: right;
      min-width: 52px;
      font-size: 0.9rem;
      color: var(--text-primary);
      flex-shrink: 0;
    }
  }
</style>
